import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useStoreRehydrated  } from 'easy-peasy';
import PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { httpGetEnums } from "services/enums";
import { Spinner } from "stories/components"
import { Container, Row, Col } from "stories/layout";
import { useAuthContext } from "contexts/AuthContext";
import { fi } from "date-fns/locale";
import { httpGetTags } from "services/tags";
import { httpGetInstanceSettings } from "services/instanceSettings";



export const AppContext = createContext({});

// Server defaults of the instance feature flags (admin_data INSTANCE_FEATURE_FLAGS),
// used if the flags cannot be loaded.
const DEFAULT_INSTANCE_FEATURES = { crypto: true };

export const AppProvider = ({ children }) => {
  const isRehydrated = useStoreRehydrated(); // is persist data loaded
  const { t } = useTranslation();
  const [enums, setEnums] = useState();
  // Instance feature flags (AdminParams of this instance); null until loaded. If the request
  // fails, fall back to shown (behaviour before the flags).
  const [instanceFeatures, setInstanceFeatures] = useState(null);
  const { isAuthenticated, loading, loadingMyUser, myUser } = useAuthContext();

  // const setBasicDataObjects = useStoreActions((actions) => actions.stocks.setObjects);
  const getBasicDataObjects = useStoreActions((actions) => actions.stocks.getObjects);
  const getPrivateStocks = useStoreActions((actions) => actions.privateStocks.getObjects);
  const getPrivateInvestmentFunds = useStoreActions((actions) => actions.privateInvestmentFunds.getObjects);
  const getLicenses = useStoreActions((actions) => actions.licenses.getLicenses);
  const getNewFeatures = useStoreActions((actions) => actions.newFeatures.getNewFeatures);
  const getFrontpageInfo = useStoreActions((actions) => actions.frontpageInfo.getFrontpageInfo);
  const getHelptexts = useStoreActions((actions) => actions.helptexts.getHelptexts);
  const getStockMarkets = useStoreActions((actions) => actions.stockMarkets.getStockMarkets);
  const getSectors = useStoreActions((actions) => actions.sectors.getSectors);
  const getTags = useStoreActions((actions) => actions.tags.getTags);
  const sectors = useStoreState((state) => state.sectors?.sectors);
  const getCompanies = useStoreActions((actions) => actions.companies.getCompanies);
  const getPortfoliosSummary = useStoreActions((actions) => actions.portfoliosSummary.getPortfoliosSummary);
  const getUnmadeDividendTransactions = useStoreActions((actions) => actions.unmadeDividendTransactions.getUnmadeDividendTransactions);
  const getUnmadeSplitTransactions = useStoreActions((actions) => actions.unmadeSplitTransactions.getUnmadeSplitTransactions);

  
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);
  const licenses = useStoreState((state) => state.licenses?.licenses);
  const portfolio = useStoreState((state) => state.portfolio?.portfolio);
  const setPortfolio = useStoreActions((actions) => actions.portfolio.setPortfolio);
  const portfoliosLoaded = useStoreState((state) => state.portfolio?.loaded);
  const reconcileActivePortfolios = useStoreActions((actions) => actions.portfolio.reconcileActive);
  const companies = useStoreState((state) => state.companies?.companies)

  
  const getEnums = () => {
    httpGetEnums().then(response => {
      setEnums(response?.data);

    }, error => {
      console.log("getEnums error", error);
    })
  }
  
  const getInstanceSettings = () => {
    httpGetInstanceSettings().then(response => {
      setInstanceFeatures(response?.data?.features || {});
    }, error => {
      console.log("getInstanceSettings error", error);
      setInstanceFeatures(DEFAULT_INSTANCE_FEATURES);
    })
  }

  // Hidden until loaded, so a hidden feature does not flash.
  const isFeatureEnabled = (feature) => instanceFeatures?.[feature] === true

  // Security (sub)types that an instance flag hides from type selects.
  // Existing holdings are still shown everywhere else.
  const isSecurityTypeHidden = (key) => key === "crypto" && !isFeatureEnabled("crypto")

  const getBasicData = () => {
    const params = {
      stock_market: 'XHEL',
      ordering: 'name',
    }
    getBasicDataObjects(params)
  }

  // get portfolio name by id 
  const getPortfolioName = (id) => {
    const portfolio = portfolios && portfolios.reduce((result, portfolio) => {
      if (portfolio?.id === id) {
        result = portfolio
      }
      return result
    }, null)
    return portfolio?.name
  }

    // get portfolio currency by id 
  const getPortfolioCurrency = (id) => {
      const portfolio = portfolios && portfolios.reduce((result, portfolio) => {
        if (portfolio?.id === id) {
          result = portfolio
        }
        return result
      }, null)
      return portfolio?.currency_label
    }

  // get license name by id 
  const getLicenseName = (id) => {
    const license = licenses && licenses.reduce((result, license) => {
      if (license?.id === id) {
        result = license
      }
      return result
    }, null)
    return license?.name
  }

  // get company name by id
  const getCompanyInstance = (id) => {
    const company = companies && companies.reduce((result, company) => {
      if (company?.id === id) {
        result = company
      }
      return result
    }, null)
    return company
  }

  
  // Enum helpers
  
  const getTransactionType = (key) => {
    const transactionTypes = enums?.transactions?.transaction?.type
    if (transactionTypes && transactionTypes.length > 0) {
      const filteredObjects = transactionTypes.filter(o => o.key === key)
      if (filteredObjects && filteredObjects.length > 0) {
        return filteredObjects[0]
      }
    }
    return null;
  }

  const getSecurityType = (key) => {
    const transactionSecurityTypes = enums?.transactions?.transaction?.related_model_type
    if (transactionSecurityTypes && transactionSecurityTypes.length > 0) {
      const filteredObjects = transactionSecurityTypes.filter(o => o.key === key)
      if (filteredObjects && filteredObjects.length > 0) {
        return filteredObjects[0]
      }
    }
    return null;
  }

  const getSecurityTypeExtend = (key, type) => {
    const transactionSecurityTypes = enums?.transactions?.transaction?.related_model_type
    const subTypes = enums?.private_investment_funds?.private_investment_fund?.type

    if (transactionSecurityTypes && transactionSecurityTypes.length > 0) {
      let filteredObjects = null

      if (key == 30){
        filteredObjects = subTypes.filter(o => o.key === type)
      }
      else{
       filteredObjects = transactionSecurityTypes.filter(o => o.key === key)
      }
      if (filteredObjects && filteredObjects.length > 0) {
        return filteredObjects[0]
      }
    }
    return null;
  }

  //get stockmarket name by key
  const getStockMarketName = (key) => {
    const stockMarkets = enums?.stock?.basic_data?.stock_market
    //return stockmarket value using key
    if (stockMarkets && stockMarkets.length > 0) {  
      const filteredObjects = stockMarkets.filter(o => o.key === key)
      if (filteredObjects && filteredObjects.length > 0) {
        return filteredObjects[0].value
      }
    }
  }

  //get sector name by key
    const getSectorName = (key) => {
      
      //return stockmarket value using key
      if (sectors && sectors.length > 0) {  
        const filteredObjects = sectors.filter(o => o.id === key)
        if (filteredObjects && filteredObjects.length > 0) {
          return filteredObjects[0].name
        }
      }
    }

  
  //get user_type name by key
  const getUserTypeName = (key) => {
    const userTypes = enums?.accounts?.account_user?.user_type
    //return user_type value using key
    if (userTypes && userTypes.length > 0) {
      const filteredObjects = userTypes.filter(o => o.key === key)
      if (filteredObjects && filteredObjects.length > 0) {
        return filteredObjects[0].value
      }
    }

  }
  
  
  const getDerivativeType = (key) => {
    const derivativeTypes = enums?.options?.option?.derivative_type;
    return derivativeTypes && derivativeTypes.reduce((result, dType) => {
      if (dType?.key === key) {
        result = dType
      }
      return result
    }, null)
  }

  const getAccountTypeObject = (key) => {
    const accountTypes = enums?.accounts?.account?.type;
    return accountTypes && accountTypes.reduce((result, dType) => {
      if (dType?.key === key) {
        result = dType
      }
      return result
    }, null)
  }


  
  const getOptionsAssetType = (key) => {
    const assetTypes = enums?.options?.option?.asset_type;
    return assetTypes && assetTypes.reduce((result, assetType) => {
      if (assetType?.key === key) {
        result = assetType
      }
      return result
    }, null)
  }
  
  // AuthProvider can mount this provider while /users/me/ is still in flight (its
  // /my_account_user/ call may finish first), which fired the public preloads once
  // signed out and again when isAuthenticated flipped. Wait for the user, then load once
  // per sign-in state.
  const preloadedForAuth = useRef(null);
  useEffect(() => {
    if (loadingMyUser || preloadedForAuth.current === isAuthenticated) {
      return;
    }
    preloadedForAuth.current = isAuthenticated;
    getEnums();
    getLicenses();
    getFrontpageInfo();
    if (isAuthenticated) {
      getInstanceSettings();
      getBasicData();
      getPrivateStocks();
      getNewFeatures();
      getHelptexts();
      getCompanies();
      // getPortfoliosSummary();
      getUnmadeDividendTransactions()
      getUnmadeSplitTransactions()
      getPrivateInvestmentFunds();
      getStockMarkets();
      getSectors();
      getTags();


    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loadingMyUser]);

  // Active portfolios (#1409): decide the selection once the persisted slice, myUser and the
  // portfolio list are all loaded. Once per user and account it takes the browser's stored
  // selection first and myUser.active_portfolio only as a fallback; getPortfolios reconciles
  // again after every later fetch.
  useEffect(() => {
    if (isRehydrated && isAuthenticated && myUser?.id) {
      reconcileActivePortfolios({ myUser });
    }
    // myUser is a new object on every getMyUser; only these fields matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRehydrated, isAuthenticated, myUser?.id, myUser?.active_account, myUser?.active_portfolio, portfoliosLoaded]);

  return isRehydrated ? (
      <AppContext.Provider
        value={{
          enums,
          getTransactionType,
          getOptionsAssetType,
          getDerivativeType,
          getPortfolioName,
          getPortfolioCurrency,
          getUserTypeName,
          getStockMarketName,
          getSectorName,
          getSecurityType,
          getSecurityTypeExtend,
          getLicenseName,
          getAccountTypeObject,
          getCompanyInstance,
          instanceFeatures,
          isFeatureEnabled,
          isSecurityTypeHidden,
        }}
      >
        {children}
      </AppContext.Provider>
    ) :            
      <Container className="d-flex align-items-center justify-content-center" style={{ height: "100vh" }}>
        <Spinner animation="border" role="status" className="text-secondary"/>
      </Container>
  
};

AppContext.defaultProps = {};
AppContext.propTypes = {
  children: PropTypes.node.isRequired
};

export const useAppContext = () =>
  useContext(AppContext);
